// (c) chaehongee. Confidential and proprietary.

import type { ReactElement } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAppSelector, useAppDispatch } from '../../store/hooks/hooks'
import { ReactFlow, Background } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import styles from './Tree.module.css'
import { GitFork as TreeIcon, EyeOff as HideIcon } from 'lucide-react'
import { buildGraph } from './graph'
import IconButton from '../IconButton/IconButton'
import { setHovered, setNodes as setSelectedNodes, toggleNode } from '../../store/slices/selectionSlice'
import { addItem as addCanvasItem, removeItem as removeCanvasItem } from '../../store/slices/canvasSlice'
import { removeNode } from '../../store/slices/sessionSlice'
import { deleteNode as apiDeleteNode } from '../../services/node'
import { ReactFlowNode } from './ReactFlowNode'
import { CustomEdge } from './CustomEdge'
import { selectActiveSession, selectActiveSessionId } from '../../store/selectors/sessionSelectors'
import { selectSelectedNodeIdsBySessionId } from '../../store/selectors/selectionSelectors'

const nodeTypes = { image: ReactFlowNode }
const edgeTypes = { custom: CustomEdge }
const FIT_VIEW_PADDING = 0.3

export function Tree(): ReactElement {
    const activeSessionId = useAppSelector(selectActiveSessionId)
    const session = useAppSelector(selectActiveSession)
    const selected = useAppSelector((s) => (
        activeSessionId ? selectSelectedNodeIdsBySessionId(s, activeSessionId) : []
    ))
    const selectedRef = useRef(selected)
    selectedRef.current = selected
    const dispatch = useAppDispatch()
    const rfRef = useRef<any>(null)
    const didFitRef = useRef(false)
    const initialLayout = useMemo(() => getDefaultMinimapLayout(), [])
    const [isOpen, setIsOpen] = useState<boolean>(initialLayout.isOpen)
    const [size, setSize] = useState<{ width: number; height: number }>(
        initialLayout.size,
    )
    const [pos, setPos] = useState<{ x: number; y: number }>(initialLayout.pos)
    const posRef = useRef<{ x: number; y: number }>(pos)
    const sizeRef = useRef<{ width: number; height: number }>(size)
    const resizingRef = useRef<{
        startX: number
        startY: number
        startW: number
        startH: number
        baseX: number
        baseY: number
        dir: 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'
        active: boolean
    }>({ startX: 0, startY: 0, startW: 0, startH: 0, baseX: 0, baseY: 0, dir: 'se', active: false })
    const draggingRef = useRef<{
        startX: number
        startY: number
        baseX: number
        baseY: number
        active: boolean
    }>({ startX: 0, startY: 0, baseX: 0, baseY: 0, active: false })

    const { nodes, edges } = useMemo(() => {
        if (!session) return { nodes: [], edges: [] }
        return buildGraph(session)
    }, [session?.id, session?.nodes, session?.commits])

    const flowKey = session?.id ?? 'no-session'

    useEffect(() => {
        const onResize = () => {
            try {
                rfRef.current?.fitView?.({ padding: FIT_VIEW_PADDING })
            } catch { }

            // Clamp position within viewport on resize.
            const { vw, vh } = getViewport()
            const padding = 8
            const currentSize = sizeRef.current
            setPos((p) => ({
                x: Math.max(
                    padding,
                    Math.min(vw - currentSize.width - padding, p.x),
                ),
                y: Math.max(
                    padding,
                    Math.min(vh - currentSize.height - padding, p.y),
                ),
            }))
        }
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [])

    useEffect(() => {
        if (!didFitRef.current && session) {
            didFitRef.current = true
            try { rfRef.current?.fitView?.({ padding: FIT_VIEW_PADDING }) } catch { }
        }
    }, [session?.id])

    // Re-center on graph changes (nodes/edges update after refresh or new node)
    useEffect(() => {
        try {
            if (isOpen) rfRef.current?.fitView?.({ padding: FIT_VIEW_PADDING })
        } catch { }
    }, [nodes.length, edges.length, isOpen])

    const clearHoverTimeoutRef = useRef<number | null>(null)

    const clearHoverSoon = useCallback(() => {
        if (clearHoverTimeoutRef.current) window.clearTimeout(clearHoverTimeoutRef.current)
        clearHoverTimeoutRef.current = window.setTimeout(() => {
            if (!activeSessionId) return
            dispatch(setHovered({ sessionId: activeSessionId, hoveredNodeId: null }))
        }, 120)
    }, [dispatch, activeSessionId])

    const handleEnter = useCallback((_: any, node: any) => {
        if (clearHoverTimeoutRef.current) window.clearTimeout(clearHoverTimeoutRef.current)
        if (!activeSessionId) return
        dispatch(setHovered({ sessionId: activeSessionId, hoveredNodeId: node?.id }))
    }, [dispatch, activeSessionId])

    const handleMove = useCallback((_: any, node: any) => {
        if (clearHoverTimeoutRef.current) window.clearTimeout(clearHoverTimeoutRef.current)
        if (!activeSessionId) return
        dispatch(setHovered({ sessionId: activeSessionId, hoveredNodeId: node?.id }))
    }, [dispatch, activeSessionId])

    const handleLeave = useCallback(() => {
        clearHoverSoon()
    }, [clearHoverSoon])

    // Context menu state
    const [menu, setMenu] = useState<{ x: number; y: number; nodeId: string } | null>(null)
    useEffect(() => {
        const onGlobal = () => setMenu(null)
        window.addEventListener('click', onGlobal)
        return () => {
            window.removeEventListener('click', onGlobal)
        }
    }, [])

    const onNodeContextMenu = useCallback((e: React.MouseEvent, node: any) => {
        e.preventDefault()
        e.stopPropagation()
        setMenu({ x: e.clientX, y: e.clientY, nodeId: node.id })
    }, [])

    const handleDelete = useCallback(async () => {
        if (!activeSessionId) return
        if (!menu) return
        const nodeId = menu.nodeId
        setMenu(null)
        // Optimistic update first
        dispatch(removeNode({ sessionId: activeSessionId, nodeId }))
        dispatch(removeCanvasItem({ sessionId: activeSessionId, itemId: nodeId }))
        try {
            const next = selectedRef.current.filter((id) => id !== nodeId)
            dispatch(setSelectedNodes({ sessionId: activeSessionId, nodeIds: next }))
            dispatch(setHovered({ sessionId: activeSessionId, hoveredNodeId: null }))
        } catch { }
        // Then call API; on failure, reconcile by refetching session
        try {
            await apiDeleteNode(nodeId)
        } catch (err) {
            // swallow; future refresh or merges will reconcile
        }
    }, [menu, dispatch, activeSessionId])

    const handlePaneLeave = useCallback(() => {
        clearHoverSoon()
    }, [clearHoverSoon])

    const addAndSelectNode = useCallback((node: any) => {
        if (!activeSessionId) return
        try {
            const src = node?.data?.image_path
            const id = node?.id
            if (id && src) {
                dispatch(addCanvasItem({ sessionId: activeSessionId, item: { id, src } }))
                // Toggle selection; reducer caps at two selections and supports unselect on reclick
                dispatch(toggleNode({ sessionId: activeSessionId, nodeId: id }))
                // Tell Canvas to zoom+pan to this node
                window.dispatchEvent(
                    new CustomEvent('canvas:focus-node', {
                        detail: { nodeId: id, sessionId: activeSessionId },
                    })
                )
            }
        } catch { }
    }, [dispatch, activeSessionId])

    useEffect(() => {
        posRef.current = pos
    }, [pos])
    useEffect(() => {
        sizeRef.current = size
    }, [size])

    // Default layout whenever we enter a session (no persistence).
    // (No persistence across sessions/reloads.)
    useEffect(() => {
        if (!session?.id) return
        const next = getDefaultMinimapLayout()
        setIsOpen(next.isOpen)
        setSize(next.size)
        setPos(next.pos)
    }, [session?.id])

    const startResize = useCallback((e: React.MouseEvent<HTMLDivElement>, dir: 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw') => {
        e.preventDefault()
        e.stopPropagation()
        const base = posRef.current
        resizingRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            startW: size.width,
            startH: size.height,
            baseX: base.x,
            baseY: base.y,
            dir,
            active: true,
        }
        const onMove = (ev: MouseEvent) => {
            if (!resizingRef.current.active) return
            const dx = ev.clientX - resizingRef.current.startX
            const dy = ev.clientY - resizingRef.current.startY
            const vw = Math.max(320, window.innerWidth || 1024)
            const vh = Math.max(240, window.innerHeight || 768)
            const padding = 8
            const maxW = Math.max(200, vw - 2 * padding)
            const maxH = Math.max(160, vh - 2 * padding)

            let newW = resizingRef.current.startW
            let newH = resizingRef.current.startH
            let newX = resizingRef.current.baseX
            let newY = resizingRef.current.baseY

            const dirNow = resizingRef.current.dir
            const has = (ch: string) => dirNow.indexOf(ch) !== -1

            if (has('e')) {
                newW = resizingRef.current.startW + dx
            }
            if (has('s')) {
                newH = resizingRef.current.startH + dy
            }
            if (has('w')) {
                newW = resizingRef.current.startW - dx
                newX = resizingRef.current.baseX + dx
            }
            if (has('n')) {
                newH = resizingRef.current.startH - dy
                newY = resizingRef.current.baseY + dy
            }

            // clamp size
            newW = Math.max(160, Math.min(maxW, newW))
            newH = Math.max(120, Math.min(maxH, newH))

            // clamp position to viewport
            newX = Math.max(padding, Math.min(vw - newW - padding, newX))
            newY = Math.max(padding, Math.min(vh - newH - padding, newY))

            setSize({ width: newW, height: newH })
            setPos({ x: newX, y: newY })
        }
        const onUp = () => {
            resizingRef.current.active = false
            window.removeEventListener('mousemove', onMove)
            window.removeEventListener('mouseup', onUp)
        }
        window.addEventListener('mousemove', onMove)
        window.addEventListener('mouseup', onUp)
    }, [size])

    const startDrag = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
        const base = posRef.current
        draggingRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            baseX: base.x,
            baseY: base.y,
            active: true,
        }
        const onMove = (ev: MouseEvent) => {
            if (!draggingRef.current.active) return
            const dx = ev.clientX - draggingRef.current.startX
            const dy = ev.clientY - draggingRef.current.startY
            const vw = Math.max(320, window.innerWidth || 1024)
            const vh = Math.max(240, window.innerHeight || 768)
            const nx = Math.max(8, Math.min(vw - size.width - 8, draggingRef.current.baseX + dx))
            const ny = Math.max(8, Math.min(vh - size.height - 8, draggingRef.current.baseY + dy))
            setPos({ x: nx, y: ny })
        }
        const onUp = () => {
            draggingRef.current.active = false
            window.removeEventListener('mousemove', onMove)
            window.removeEventListener('mouseup', onUp)
        }
        window.addEventListener('mousemove', onMove)
        window.addEventListener('mouseup', onUp)
    }, [size])

    return (
        <div className={styles.root}>
            {!isOpen && (
                <IconButton className={styles.collapsedButton} onClick={() => setIsOpen(true)} title="Show minimap" aria-label="Show minimap">
                    <TreeIcon className={styles.icon} aria-hidden="true" />
                </IconButton>
            )}
            {isOpen && (
                <div
                    className={styles.panel}
                    style={{
                        width: size.width,
                        height: size.height,
                        top: pos.y,
                        left: pos.x,
                    }}
                >
                    <div className={styles.header} onMouseDown={startDrag}>
                        <span className={styles.title}>minimap</span>
                        <IconButton className={styles.headerBtn} onClick={() => setIsOpen(false)} title="Hide minimap" aria-label="Hide minimap">
                            <HideIcon className={styles.icon} aria-hidden="true" />
                        </IconButton>
                    </div>
                    <div className={styles.flowContainer}>
                        {session ? (
                            <ReactFlow
                                key={flowKey}
                                ref={rfRef}
                                className={styles.flow}
                                nodes={nodes}
                                edges={edges}
                                nodeTypes={nodeTypes}
                                edgeTypes={edgeTypes}
                                onNodeClick={(_, node) => addAndSelectNode(node)}
                                onNodeDoubleClick={(_, node) => addAndSelectNode(node)}
                                onNodeContextMenu={onNodeContextMenu}
                                onNodeMouseEnter={handleEnter}
                                onNodeMouseLeave={handleLeave}
                                onNodeMouseMove={handleMove}
                                onPaneMouseLeave={handlePaneLeave}
                                fitView
                                fitViewOptions={{ padding: FIT_VIEW_PADDING }}
                                proOptions={{ hideAttribution: true }}
                                nodesDraggable={true}
                            >
                                <Background />
                            </ReactFlow>
                        ) : (
                            <div className={styles.placeholder}>No session loaded</div>
                        )}
                    </div>
                    {/* Resize handles: corners */}
                    <div className={styles.resizeCornerTL} onMouseDown={(e) => startResize(e as any, 'nw')} />
                    <div className={styles.resizeCornerTR} onMouseDown={(e) => startResize(e as any, 'ne')} />
                    <div className={styles.resizeCornerBL} onMouseDown={(e) => startResize(e as any, 'sw')} />
                    <div className={styles.resizeCornerBR} onMouseDown={(e) => startResize(e as any, 'se')} />
                    {/* Resize handles: edges */}
                    <div className={styles.resizeEdgeTop} onMouseDown={(e) => startResize(e as any, 'n')} />
                    <div className={styles.resizeEdgeRight} onMouseDown={(e) => startResize(e as any, 'e')} />
                    <div className={styles.resizeEdgeBottom} onMouseDown={(e) => startResize(e as any, 's')} />
                    <div className={styles.resizeEdgeLeft} onMouseDown={(e) => startResize(e as any, 'w')} />
                </div>
            )}
            {menu && (
                <div
                    className={styles.contextMenu}
                    style={{ top: menu.y, left: menu.x }}
                    onClick={(e) => e.stopPropagation()}
                    onContextMenu={(e) => { e.preventDefault(); e.stopPropagation() }}
                >
                    <div className={styles.contextItem} onClick={handleDelete}>Delete</div>
                </div>
            )}
        </div>
    )
}

export default Tree

function getViewport(): { vw: number; vh: number } {
    if (typeof window === 'undefined') return { vw: 1024, vh: 768 }
    return {
        vw: Math.max(320, window.innerWidth || 1024),
        vh: Math.max(240, window.innerHeight || 768),
    }
}

function getDefaultMinimapLayout(): {
    size: { width: number; height: number }
    pos: { x: number; y: number }
    isOpen: boolean
} {
    const { vw, vh } = getViewport()
    const padding = 8
    const isOpen = !isViewportTooSmall(vw, vh)

    // Default: bottom-right, 15% viewport width, 4:3, capped at 250px.
    const width = Math.max(
        160,
        Math.min(250, Math.min(vw - 2 * padding, Math.round(vw * 0.15))),
    )
    const height = Math.max(
        120,
        Math.min(vh - 2 * padding, Math.round(width * 0.75)),
    )

    return {
        size: { width, height },
        pos: { x: vw - width - padding, y: vh - height - padding },
        isOpen,
    }
}

function isViewportTooSmall(vw: number, vh: number): boolean {
    return vw < 640 || vh < 480
}


